import React, { useState, FormEvent, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { validatePasswordResetToken, resetPassword } from '../services/apiService';

const PasswordResetForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [validating, setValidating] = useState<boolean>(true);
  const [tokenValid, setTokenValid] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(() => {
    document.title = 'Passwort zurücksetzen - Kiwi Overtime Tracker';

    const validateToken = async () => {
      if (!token) {
        setError('Ungültiger Token');
        setValidating(false);
        return;
      }

      try {
        const result = await validatePasswordResetToken(token);
        if (result.valid) {
          setTokenValid(true);
        } else {
          setError('Dieser Link ist ungültig oder abgelaufen.');
        }
      } catch (error) {
        setError('Fehler beim Validieren des Tokens. Bitte versuchen Sie es erneut.');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(token!, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      if (error.response?.status === 401) {
        setError('Dieser Link ist ungültig oder abgelaufen.');
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Fehler beim Zurücksetzen des Passworts. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1 className="login-title">Passwort zurücksetzen</h1>
          <p>Token wird validiert...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1 className="login-title">Passwort zurücksetzen</h1>
          <div className="error-message">{error}</div>
          <button
            onClick={() => navigate('/login')}
            className="login-button"
            style={{ marginTop: '20px' }}
          >
            Zurück zum Login
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1 className="login-title">Passwort zurückgesetzt</h1>
          <p style={{ color: 'green', marginBottom: '20px' }}>
            Ihr Passwort wurde erfolgreich zurückgesetzt. Sie werden in Kürze zur Login-Seite weitergeleitet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="login-title">Passwort zurücksetzen</h1>
        <h2 className="login-subtitle">Neues Passwort festlegen</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="newPassword" className="form-label">Neues Passwort:</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              placeholder="Mindestens 6 Zeichen"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">Passwort bestätigen:</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              placeholder="Passwort wiederholen"
              className="form-input"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            disabled={loading}
            className="login-button"
          >
            {loading ? 'Passwort wird zurückgesetzt...' : 'Passwort zurücksetzen'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PasswordResetForm;
